/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import React, { Ref, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import ReactMarkdown from 'react-markdown';
import mergeRefs from 'react-merge-refs';
import { v4 as uuid } from 'uuid';

import { ButtonTypes, StandardButton } from '@eika/button';

import Ikon from '../../components/ikon';
import { FocusableHtmlElement, useFocusRef } from '../hooks';
import { isCurrentSporsmalValid } from '../reducer/current-steg';
import { addVedleggToSvar, deleteVedleggFromSvar } from '../reducer/sporsmalsflyt-actions';
import { useSporsmalsflytDispatch } from '../reducer/sporsmalsflytDispatchProvider';
import FileValidation, {
    defaultVarslinger,
    getVarslinger,
    validerValgteFiler,
    Varslinger,
} from './file-validation/file-validation';
import { CurrentFilopplastingsporsmal, Vedlegg, vedleggWithFileToVedlegg } from './filopplasting-types';

import './filopplasting-vedlegg.scss';
import './filopplasting.scss';

interface UploadedFileRowProps {
    file: Vedlegg;
    onDelete: () => void;
}

const UploadedFileRow = ({ file, onDelete }: UploadedFileRowProps) => (
    <li className="file-upload-list-element">
        <Ikon ikon="ikon-checkmark-liste-24" className="file-upload-list-element-ikon" />
        <div className="file-upload-list-element-file-name">{file.fileInfo.name}</div>
        <StandardButton type="button" buttonType={ButtonTypes.STANDARD_NEGATIVE} onClick={onDelete}>
            Slett
        </StandardButton>
    </li>
);

interface Props {
    currentSporsmal: CurrentFilopplastingsporsmal;
    showFeilmelding: boolean;
    focusOnMount: boolean;
    errorRef?: Ref<FocusableHtmlElement>;
}

export const Filopplasting = ({ currentSporsmal, showFeilmelding, errorRef, focusOnMount = true }: Props) => {
    const { filer } = currentSporsmal;

    const [varslinger, setVarslinger] = useState<Varslinger>(defaultVarslinger());
    const uploadButtonRef = useFocusRef<HTMLButtonElement>(focusOnMount);
    const dispatch = useSporsmalsflytDispatch();

    const addToState = (chosenFiles: File[]): void => {
        setVarslinger(getVarslinger(chosenFiles, filer));
        const filesToUpload = validerValgteFiler(chosenFiles, filer).map((file: File) => ({
            id: uuid(),
            fil: file,
            url: URL.createObjectURL(file),
        }));
        dispatch(addVedleggToSvar(vedleggWithFileToVedlegg(filesToUpload)));
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop: addToState });

    return (
        <div className="file-upload-wrapper container__standard-width">
            <div className="file-upload">
                <h2 className="step-header step-header__with-ingress">{currentSporsmal.sporsmal.tittel}</h2>
                <ReactMarkdown>{currentSporsmal.sporsmal.ingress}</ReactMarkdown>
                <div
                    className={classNames(
                        { 'dokumentopplasting--dragActive': isDragActive },
                        { 'dokumentopplasting--dragNotActive': !isDragActive },
                        'filopplaster',
                    )}
                    {...getRootProps()}
                >
                    <input {...getInputProps()} onClick={() => setVarslinger(defaultVarslinger())} />
                    <div className="filopplaster__dropzone">
                        <span className="filopplaster__dropzone__buttons">
                            <p>{currentSporsmal.sporsmal.tekst}, eller</p>
                            <StandardButton
                                type="button"
                                buttonType={ButtonTypes.STANDARD_NEGATIVE}
                                ref={mergeRefs([uploadButtonRef, errorRef])}
                            >
                                Velg fil
                            </StandardButton>
                        </span>
                    </div>
                </div>
                {varslinger && (
                    <div className="file-upload-varslinger">
                        <FileValidation varslinger={varslinger} />
                    </div>
                )}

                {showFeilmelding && !isCurrentSporsmalValid(currentSporsmal) && (
                    <span className="input-text-feilmelding" aria-live="polite">
                        Du må laste opp minst ett dokument.{' '}
                    </span>
                )}

                {filer && filer.length > 0 && (
                    <ul className="file-upload-list">
                        {filer.map((fil) => (
                            <UploadedFileRow
                                key={fil.id}
                                file={fil}
                                onDelete={() => {
                                    dispatch(deleteVedleggFromSvar([fil]));
                                }}
                            />
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};
